/**
 * Shipper HQ
 *
 * @copyright Copyright (c) 2026 Zowta Ltd (http://www.shipperhq.com)
 * Copyright, 2026, Zowta, LLC - US license
 */

import React, { useEffect } from 'react'
import PlatformButton from './PlatformButton'
import LoginFormView from './LoginFormView'
import LoginHeader from './LoginHeader'
import usePlatformsList from './hooks/usePlatformsList'
import { usePlatform } from './hooks/usePlatform'
import useSubmitForm from 'hooks/useSubmitForm'
import { useFlashMessages } from '../../../hooks/useFlashMessages'
import Alert from 'components/ui/Alert'
import chunk from 'lodash/chunk'
import * as Constants from './constants'

/**
 * LoginForm Properties
 */

export default function LoginForm(props) {
    const [selectedPlatform, setSelectedPlatform] = usePlatform()
    const platforms = usePlatformsList()
    const submitForm = useSubmitForm('/user/sign_in')
    const flashMessages = useFlashMessages()

    const onPlatformSelected = (platform) => setSelectedPlatform(platform)

    // DOMAIN: Shopify App Store requirement 2.3.1 forbids asking merchants to
    // manually type their myshopify.com URL. selectedPlatform can be set both
    // by clicking the Shopify button and by a `?platform=shopify` query param
    // (see usePlatform), so the redirect is handled centrally here rather than
    // in the button's onClick handler.
    useEffect(() => {
        if (selectedPlatform === Constants.SHOPIFY) {
            window.location.href = Constants.SHOPIFY_ADMIN_APP_URL
        }
    }, [selectedPlatform])

    if (selectedPlatform === Constants.SHOPIFY) {
        return null
    }

    if (selectedPlatform) {
        return (
            <LoginFormView
                platform={selectedPlatform}
                onNavigateBack={() => setSelectedPlatform(null)}
                loading={submitForm.loading}
                serverError={submitForm.error}
            />
        )
    }

    const messages = (
        <>
            {flashMessages.map(([type, message]) => (
                <Alert
                    type={type === 'notice' ? 'success' : 'danger'}
                    key={message}
                    message={message}
                />
            ))}
        </>
    )

    return (
        <div>
            <LoginHeader
                title="Welcome back"
                subtitle="Select your platform to login"
                columns={3}
            />
            {messages}
            <div className="platform-selector">
                {chunk(platforms, 4).map((chunkPlatforms) =>
                    chunkPlatforms.map((platform, index) => (
                        <PlatformButton
                            key={`${platform}-${index}`}
                            platform={platform}
                            onClick={onPlatformSelected}
                        />
                    ))
                )}
            </div>
            <footer>
                <small>
                    Using a custom or unlisted platform?{' '}
                    <a href="/login?platform=generic" data-testid="generic-login-link">Login</a>
                </small>
                <br />
                <small>
                    Don't have an account yet?{' '}
                    <a href="/signup">Sign Up Free</a>
                </small>
            </footer>
        </div>
    )
}
